import { QueryClient, UseMutateFunction, useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import {
	editGoldenSettings,
	resetGoldenSettings,
	setGoldenSettings,
} from '@api/goldenSettings';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { TGoldenSettingsOptionDTO, TGoldenSettingsSetTypeDTO } from '@interfaces/cmModule/dto/goldenSettingsDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';

import {
	ESnackbarTypes,
} from '@enums/common';

import { refetchOptions } from '@constants/common';
import {
	CONSISTENCY_FETCH,
	GOLDEN_SETTING,
	GOLDEN_SETTING_EDIT,
	GOLDEN_SETTING_INFO,
	GOLDEN_SETTING_RESET,
	GOLDEN_SETTING_SET,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

const clearConsistencyTableData = (queryClient: QueryClient) => {
	queryClient.setQueriesData({ queryKey: [CONSISTENCY_FETCH] }, []);
};

interface IUseGoldenSettingsMutationsReturnType {
	editGoldenSettingsConfig: UseMutateFunction<any, any, TGoldenSettingsSetTypeDTO, any>;
	setGoldenSettingsConfig: UseMutateFunction<any, any, TGoldenSettingsSetTypeDTO, any>;
	resetGoldenSettingsConfig: UseMutateFunction<any, any, TGoldenSettingsSetTypeDTO, any>;
	isLoadingMutations: boolean;
}

export interface IUseGoldenSettingsMutationsHandlers {
	closeGoldenSettingForm?: () => void;
}

const useGoldenSettingsMutations = (
	params: IUseGoldenSettingsMutationsHandlers & TGoldenSettingsOptionDTO,
): IUseGoldenSettingsMutationsReturnType => {

	const dispatch = useAppDispatch();

	const queryClient = useQueryClient();

	const {
		vendor,
		technology,
		nodeType,
		closeGoldenSettingForm,
	} = params;

	const {
		mutate: setGoldenSettingsConfig,
		isLoading: isLoadingSetAsGoldenSettings,
	} = useMutation<TGoldenSettingsSetTypeDTO>({
		mutationKey: [GOLDEN_SETTING_SET],
		mutationFn: setGoldenSettings,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.goldenSettingsSet,
				type: ESnackbarTypes.success,
			}));

			if (closeGoldenSettingForm) {
				closeGoldenSettingForm();
			}

			clearConsistencyTableData(queryClient);

			return [
				queryClient.invalidateQueries([GOLDEN_SETTING]),
				queryClient.invalidateQueries([GOLDEN_SETTING_INFO]),
			];
		},
		onError: (err: IResponseError) => {
			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: resetGoldenSettingsConfig,
		isLoading: isLoadingClearGoldenSettings,
	} = useMutation<TGoldenSettingsOptionDTO>({
		mutationKey: [GOLDEN_SETTING_RESET],
		mutationFn: resetGoldenSettings,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.goldenSettingsReset,
				type: ESnackbarTypes.success,
			}));

			if (closeGoldenSettingForm) {
				closeGoldenSettingForm();
			}

			clearConsistencyTableData(queryClient);

			return [
				queryClient.resetQueries({ queryKey: [GOLDEN_SETTING_INFO] }),
				queryClient.resetQueries({ queryKey: [GOLDEN_SETTING] }),
			];
		},
		onError: (err: IResponseError) => {
			handleErrorFromMessage(err);
		},
	});

	const {
		mutate: editGoldenSettingsConfig,
		isLoading: isLoadingEditGoldenSettingsData,
	} = useMutation<TGoldenSettingsSetTypeDTO>({
		mutationKey: [GOLDEN_SETTING_EDIT, vendor, technology, nodeType],
		mutationFn: editGoldenSettings,
		onSuccess: () => {
			dispatch(setSnackbar({
				message: responseMessages.goldenSettingsSet,
				type: ESnackbarTypes.success,
			}));

			if (closeGoldenSettingForm) {
				closeGoldenSettingForm();
			}

			clearConsistencyTableData(queryClient);

			return [
				queryClient.invalidateQueries([GOLDEN_SETTING]),
				queryClient.invalidateQueries([GOLDEN_SETTING_INFO]),
			];
		},
		...refetchOptions,
		onError: (err: IResponseError) => {
			handleErrorFromMessage(err);
		},
	});

	return {
		editGoldenSettingsConfig,
		setGoldenSettingsConfig,
		resetGoldenSettingsConfig,
		isLoadingMutations: (
			isLoadingSetAsGoldenSettings
			|| isLoadingEditGoldenSettingsData
			|| isLoadingClearGoldenSettings
		),
	};
};

export default useGoldenSettingsMutations;
